import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { GeoResult } from '../types';
import { currentPosition } from '../native';
import { Icon } from './Icon';

export interface Location { city: string | null; lat: number | null; lng: number | null }

/** One field: shows the chosen city; typing in it searches (Nominatim). "Use my location" fills it from the device. */
export function LocationPicker({ value, onChange }: { value: Location; onChange: (v: Location) => void }) {
  const [text, setText] = useState(value.city ?? '');
  const [editing, setEditing] = useState(false);
  const [hits, setHits] = useState<GeoResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => { if (!editing) setText(value.city ?? ''); }, [value.city, editing]);

  useEffect(() => {
    if (!editing) return;
    if (timer.current) window.clearTimeout(timer.current);
    const q = text.trim();
    if (q.length < 2 || q === value.city) { setHits([]); return; }
    timer.current = window.setTimeout(async () => {
      try { setHits((await api.geocode(q)).results); } catch (e: any) { setError(e.message); }
    }, 400);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [text, editing, value.city]);

  const pick = (g: GeoResult) => { onChange({ city: g.label, lat: g.lat, lng: g.lng }); setText(g.label); setHits([]); setEditing(false); };
  const useMine = async () => {
    setBusy(true);
    setError(null);
    try { pick(await api.reverseGeocode(...(await currentPosition().then((p) => [p.lat, p.lng] as const)))); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="stack">
      <div className="row">
        <div className="dropdown" style={{ flex: 1 }}>
          <input type="search" placeholder="Enter your city" value={text}
            onFocus={() => setEditing(true)}
            onBlur={() => setTimeout(() => { setEditing(false); setHits([]); }, 150)}
            onChange={(e) => setText(e.target.value)} />
          {hits.length > 0 && (
            <div className="menu">
              {hits.map((h, i) => <button type="button" key={i} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(h)}>{h.label}</button>)}
            </div>
          )}
        </div>
        <button type="button" className="btn secondary sm" onClick={useMine} disabled={busy} aria-label="Use my location">{busy ? '…' : <Icon name="pin" size={22} />}</button>
      </div>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
